/**
 * NFC Solutions — Event sync loop.
 *
 * Drains `event_queue` (populated by localWrite) toward
 * `POST /api/events/sync`. Never touches the UI. Never blocks a
 * write.
 *
 * States driven:
 *   LOCAL   → the moment we pick an event up, mark PENDING
 *   PENDING → after 2xx from the cloud, mark SYNCED (+ synced_at)
 *   PENDING → after a network error, stays PENDING; next tick retries
 *   PENDING → after a 4xx (permanent rejection), mark FAILED so the
 *             owner can inspect it in Developer Tools
 *
 * The loop is triggered by:
 *   • App boot (once, after 3s so UI hydration wins)
 *   • Every `LOCAL_EVENT_APPLIED` DOM event (i.e. every localWrite)
 *   • The browser `online` event
 *   • A periodic 30s heartbeat (belt-and-braces)
 */

import axios from "axios";
import { EventLifecycle } from "./events";
import {
  listQueuedEvents,
  markEventStatus,
  discardSyncedEvents,
  LOCAL_EVENT_APPLIED,
} from "./eventEngine";
import { putOne, deleteOne } from "./localDb";

const BATCH_SIZE     = 20;
const HEARTBEAT_MS   = 30 * 1000;
const BACKOFF_INITIAL_MS = 5 * 1000;
const BACKOFF_MAX_MS = 60 * 1000;

export const EVENT_SYNC_FLUSHED = "nfcs:event-sync-flushed";

let _armed = false;
let _draining = false;
// Session 41.4 — a localWrite that lands WHILE a batch is in flight used to
// be dropped by the `_draining` guard and wait for the 30 s heartbeat (long
// enough for the payment fast-path to reach the server before the order it
// pays for). Remember the missed tick and drain again as soon as we finish.
let _rerunRequested = false;
let _backoff  = 0;
let _heartbeat = null;
let _tokenSupplier = null;

/**
 * Configure the loop with a function that returns the current auth
 * token (any role works — the /api/events/sync endpoint is
 * role-scoped by the token's business). Passing `null` disables the
 * sync (used by Developer Tools "Simulate Offline").
 */
export function configureEventSync({ tokenSupplier } = {}) {
  _tokenSupplier = typeof tokenSupplier === "function" ? tokenSupplier : null;
}

function _backendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
}

function _authHeaders() {
  const token = _tokenSupplier ? _tokenSupplier() : null;
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
}

async function _flushOnce() {
  if (_draining) { _rerunRequested = true; return; }
  _draining = true;
  try {
    const headers = _authHeaders();
    if (!headers) return; // no session yet — try again next tick
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;

    const pending = await listQueuedEvents({ status: EventLifecycle.LOCAL });
    const retrying = await listQueuedEvents({ status: EventLifecycle.PENDING });
    // Session 41.5 — send in CAUSAL order (by the envelope timestamp), not
    // "new rows first, retries after". Otherwise a payment written while the
    // order it settles was still retrying (PENDING) would reach the server
    // AHEAD of that order and be refused as an overpayment on an empty table.
    const byTs = (a, b) => String((a && (a.ts || (a._meta && a._meta.queued_at))) || "").localeCompare(String((b && (b.ts || (b._meta && b._meta.queued_at))) || ""));
    const queue = [...pending, ...retrying].sort(byTs).slice(0, BATCH_SIZE);
    if (queue.length === 0) {
      _backoff = 0;
      return;
    }

    // Mark PENDING before send so a mid-flight restart doesn't
    // double-submit.
    await Promise.all(queue.map((r) => markEventStatus(r.id, {
      status: EventLifecycle.PENDING,
      _meta: { pending_since: new Date().toISOString() },
    })));

    const url = `${_backendUrl()}/api/events/sync`;
    const body = { events: queue.map(_toWireEvent) };
    let response = null;
    try {
      response = await axios.post(url, body, { headers, timeout: 15000 });
    } catch (err) {
      // Network / 5xx — leave events PENDING for retry. Back off
      // so we don't hammer a broken cloud.
      _backoff = Math.min(BACKOFF_MAX_MS, Math.max(BACKOFF_INITIAL_MS, _backoff * 2 || BACKOFF_INITIAL_MS));
      return;
    }

    const results = (response && response.data && response.data.results) || [];
    const byChangeId = new Map(results.map((r) => [r.change_id, r]));
    const now = new Date().toISOString();
    let _sawDeferred = false;
    // Session 41.4 — permanent rejections, with the original envelope, so
    // the dashboards can tell the operator (and drop the local ghost row).
    const rejected = [];

    for (const row of queue) {
      const r = byChangeId.get(row.change_id);
      if (!r) {
        // Server didn't ACK this row explicitly — leave PENDING.
        continue;
      }
      if (r.status === "accepted" || r.status === "duplicate") {
        // Session 27.38 — Reconcile local optimistic IDs with the
        // authoritative server ids so the dashboard doesn't render a
        // "ghost" of the same row twice.
        //
        // When we wrote locally we used a client-generated uuid as
        // `id` (e.g. TABLE_OPENED wrote table_sessions[localId]).
        // The cloud reducer, once it processes the event, returns a
        // server-side row with its OWN id. Without cleanup, IDB
        // ends up with both the optimistic row AND the server row —
        // that's the source of the "5 tables all called Table 5"
        // reports.
        try {
          await _reconcileLocalRow(row, r);
        } catch (_) { /* best-effort */ }
        await markEventStatus(row.id, {
          status: EventLifecycle.SYNCED,
          _meta: { synced_at: now, server_status: r.status, server_id: r.server_id || null },
        });
      } else if (r.status === "rejected") {
        await markEventStatus(row.id, {
          status: EventLifecycle.FAILED,
          _meta: { failed_at: now, server_error: r.error || "rejected" },
        });
        // Session 62 — drop the optimistic IDB row too, otherwise the next
        // local merge resurrects the ghost order the server refused.
        try {
          const et = String(row.event_type || row.type || "");
          const p = row.payload || {};
          if ((et === "order_created" || et === "order_held_for_confirmation") && p.order && p.order.id) {
            await deleteOne("orders", p.order.id);
          } else if (et === "table_opened" && p.table_session && p.table_session.id) {
            await deleteOne("table_sessions", p.table_session.id);
          }
        } catch (_) { /* best-effort */ }
        rejected.push({ change_id: row.change_id, event_type: row.event_type || row.type, payload: row.payload || {}, error: r.error || r.reason || "rejected" });
      } else if (r.status === "deferred") {
        // Session 30.26 — Valid but not applicable YET (e.g. a kitchen
        // READY whose order_created is still queued on another device).
        // Stay PENDING; the next tick retries. Remember why for DevTools.
        await markEventStatus(row.id, {
          status: EventLifecycle.PENDING,
          _meta: { deferred_at: now, deferred_reason: r.reason || "deferred",
                   deferred_count: Number((row._meta || {}).deferred_count || 0) + 1 },
        });
        _sawDeferred = true;
      } else {
        // Unknown status — treat as pending, retry next tick.
      }
    }
    // Session 40 — tell open screens what just landed (stock panels
    // refresh their numbers; the Sync view updates its counters).
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(EVENT_SYNC_FLUSHED, { detail: { results, count: queue.length, rejected } }));
      }
    } catch (_) { /* noop */ }
    // A deferred row means "come back soon" — a short backoff instead of
    // the 30 s heartbeat so cross-device ordering resolves in seconds.
    _backoff = _sawDeferred ? 3000 : 0;
    if (_sawDeferred) setTimeout(() => { _tick().catch(() => {}); }, 3000);
  } catch (e) {
    // Never throw out of the loop — but log for diagnostics.
    console.warn("[eventSync] flush error:", e?.message || e);
  } finally {
    _draining = false;
    if (_rerunRequested) {
      _rerunRequested = false;
      setTimeout(() => { _tick().catch(() => {}); }, 50);
    }
  }
}

/**
 * Session 41.4 — Are there still-unsynced events (LOCAL / PENDING) that
 * touch this table? Used by the payment fast-path so a tender is never
 * POSTed to the cloud ahead of the order it settles (the server would
 * refuse it as an "overpayment" against a table it thinks is empty).
 */
/**
 * Session 73 — ids of every row this device still has QUEUED (LOCAL /
 * PENDING): optimistic order ids, their change_ids, and the session ids of
 * queued table_opened events. The dashboard merge keeps these visible no
 * matter how old they are; a FAILED (rejected) event is excluded because
 * the rejection handler already removes its row and tells the operator.
 */
export async function queuedRowIds() {
  const out = { orderIds: new Set(), changeIds: new Set(), sessionIds: new Set() };
  try {
    const rows = await listQueuedEvents();
    for (const r of rows) {
      const st = r && r._meta && r._meta.status;
      if (st !== EventLifecycle.LOCAL && st !== EventLifecycle.PENDING) continue;
      const et = String(r.event_type || r.type || "");
      const p = r.payload || {};
      if (r.change_id) out.changeIds.add(String(r.change_id));
      if ((et === "order_created" || et === "order_held_for_confirmation") && p.order && p.order.id) {
        out.orderIds.add(String(p.order.id));
      } else if (et === "table_opened" && p.table_session && p.table_session.id) {
        out.sessionIds.add(String(p.table_session.id));
      }
    }
  } catch (_) { /* best effort */ }
  return out;
}

export async function hasUnsyncedEventsForSession(sessionId) {
  if (!sessionId) return false;
  try {
    const rows = await listQueuedEvents();
    return rows.some((r) => {
      const st = r && r._meta && r._meta.status;
      if (st !== EventLifecycle.LOCAL && st !== EventLifecycle.PENDING) return false;
      const p = r.payload || {};
      const sid = p.table_session_id || (p.table_session && p.table_session.id) || (p.order && p.order.table_session_id) || null;
      return String(sid || "") === String(sessionId);
    });
  } catch (_) {
    return false;
  }
}

async function _reconcileLocalRow(row, result) {
  const evt = row || {};
  const rServer = result || {};
  const eventType = String(evt.event_type || evt.type || "");
  const payload = evt.payload || {};
  // Recover the local optimistic id (what we keyed the IDB row on
  // when we wrote it) and the authoritative server row (if the
  // server returned it in the response).
  if (eventType === "table_opened") {
    const localId = (payload.table_session && payload.table_session.id) || null;
    const serverRow = rServer.table_session || null;
    const serverId = (serverRow && serverRow.id) || rServer.server_id || null;
    if (!serverId) return;
    if (localId && localId !== serverId) {
      try { await deleteOne("table_sessions", localId); } catch (_) { /* noop */ }
    }
    if (serverRow) await putOne("table_sessions", serverRow);
  } else if (eventType === "order_created") {
    const localOrder = payload.order || {};
    const localId = localOrder.id || null;
    const serverOrder = rServer.order || null;
    const serverId = (serverOrder && serverOrder.id) || rServer.server_id || null;
    if (!serverId) return;
    if (localId && localId !== serverId) {
      try { await deleteOne("orders", localId); } catch (_) { /* noop */ }
    }
    if (serverOrder) await putOne("orders", serverOrder);
  } else if (eventType === "table_closed" || eventType === "payment_recorded") {
    // These operate on an already-server-side session, so no id
    // reconciliation needed — but we still refresh the local row
    // with the authoritative server payload so a stale IDB copy
    // doesn't linger.
    const serverRow = rServer.table_session || null;
    if (serverRow && serverRow.id) await putOne("table_sessions", serverRow);
  }
}

function _toWireEvent(row) {
  // Strip _meta (internal to the queue), send only the envelope.
  const { _meta, id, ...envelope } = row;
  return envelope;
}

async function _tick() {
  await _flushOnce();
  try { await discardSyncedEvents(); } catch (e) { console.warn("[eventSync] discardSyncedEvents error:", e?.message || e); }
}

function _armHeartbeat() {
  if (_heartbeat) return;
  _heartbeat = setInterval(() => { _tick(); }, HEARTBEAT_MS);
}

/**
 * Arm the sync loop. Idempotent — safe to call from every dashboard
 * mount. Call from React app root once auth is available.
 */
export function armEventSync() {
  if (_armed) return;
  if (typeof window === "undefined") return;
  _armed = true;

  window.addEventListener("online", _tick);
  window.addEventListener(LOCAL_EVENT_APPLIED, () => {
    // Fire immediately (with a small delay to batch bursts of writes
    // in the same tick).
    setTimeout(_tick, 250);
  });

  _armHeartbeat();

  // Kick off after 3s so app hydration wins the CPU.
  setTimeout(_tick, 3000);
}

/** Manual drain — used by Developer Tools' "Force Cloud Synchronisation". */
export async function drainNow() {
  await _flushOnce();
}
